import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconClockPause, IconDeviceDesktop } from '@tabler/icons-react';
import { useAuth } from '../auth/useAuth';
import { usePermissions } from '../auth/usePermissions';
import { poslednyayaAktivnost, otmetitAktivnost, slushatAktivnost } from './aktivnost';
import { slovoMinut } from './obrabotkaUtils';
import { razreshitPrisutstvie, usePrisutstvie, vklyuchitPrisutstvie, type Prisutstvie } from './prisutstvie';
import { formatDuration } from './statusVocabulary';
import { soobshitOPrisutstvii } from './workplaceApi';
import { useWorkplaceState } from './useWorkplaceState';
import './okno-obrabotki.css';

/**
 * `s779` (обращение техподдержки №12, слова владельца 11.10.2026). Три окна поверх ЛЮБОЙ страницы
 * панели — живут рядом с телефоном, над маршрутами, как окно входящего звонка:
 *
 *   1. срок обработки вышел — «Завершить обработку или продлить?»; сама обработка не закрывается,
 *      без ответа за минуту станция ставит «Отошёл»;
 *   2. в «Готов» 5 минут ни движения, ни нажатий — «Вы на месте?»; как заставка: любое движение
 *      окно убирает, без движения ещё минуту — «Отошёл»;
 *   3. станция поставила «Отошёл» сама — окно висит, пока человек не вернётся к звонкам или не
 *      закроет его: статус сам не меняется.
 *
 * Окна не перекрывают страницу: карточку можно дописывать, нажатия мимо окна проходят.
 *
 * `s781` (владелец 11.10.2026: «менеджер был просто в других вкладках браузера»): тишина считается по
 * всему компьютеру, если браузер это умеет и человек разрешил (`prisutstvie.ts`). Четвёртое окно
 * просит об этом разрешении один раз: в «Готов», когда никто не звонит; «Позже» убирает его до завтра.
 */
const ZAKRYTO = 'opsmind.tel.prinuditelno.zakryto';
const POZZHE = 'opsmind.tel.prisutstvie.pozzhe';
const SOOBSHCHENO = 'opsmind.tel.prisutstvie.soobshcheno';
const KOD_PRISUTSTVIYA: Record<Prisutstvie, 'unsupported' | 'prompt' | 'granted' | 'denied'> = {
  net: 'unsupported', sprosit: 'prompt', razresheno: 'granted', zapreshcheno: 'denied',
};

function zapomnennoe(klyuch: string): string | null {
  try { return window.localStorage.getItem(klyuch); } catch { return null; }
}

function zapomnit(klyuch: string, znachenie: string): void {
  try { window.localStorage.setItem(klyuch, znachenie); } catch { /* запомнено только в этой вкладке */ }
}

/**
 * Сколько длится тишина. `s818`: не дольше самого «Готов» — разговор, обработка и нажатие «Готов» уже
 * действия человека; отметка «Готов начался» ставится эффектом ПОСЛЕ отрисовки, и без этого предела
 * первая отрисовка нового «Готов» считала тишину от старого движения.
 */
function tishinaV(status: string | null, since: string | null, now: number): number {
  const nachalo = status === 'ready' && since ? Date.parse(since) : Number.NaN;

  return now - (Number.isNaN(nachalo) ? poslednyayaAktivnost() : Math.max(poslednyayaAktivnost(), nachalo));
}

export function OknoObrabotki() {
  const { user } = useAuth();
  const { can, sectionHidden } = usePermissions();
  const allowed = user != null && can('tel.workplace') && !sectionHidden('tel-workplace');
  const workplace = useWorkplaceState(allowed);
  const { state, secondsLeft, busy } = workplace;
  const [now, setNow] = useState(() => Date.now());
  const [ostalsya, setOstalsya] = useState<string | null>(() => zapomnennoe(ZAKRYTO));
  const [pozzhe, setPozzhe] = useState<string | null>(() => zapomnennoe(POZZHE));
  const prisutstvie = usePrisutstvie();
  const soobshil = useRef(0);

  useEffect(() => {
    if (!allowed) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    const otpisat = slushatAktivnost();
    void vklyuchitPrisutstvie();

    return () => { window.clearInterval(timer); otpisat(); };
  }, [allowed]);

  // Что решил браузер этого человека — станции в журнал, один раз на каждое решение.
  const userId = user?.id ?? null;
  useEffect(() => {
    if (!allowed || prisutstvie === null || userId === null) return;
    const otmetka = `${userId}:${prisutstvie}`;
    if (zapomnennoe(SOOBSHCHENO) === otmetka) return;
    void soobshitOPrisutstvii(KOD_PRISUTSTVIYA[prisutstvie]).then(() => zapomnit(SOOBSHCHENO, otmetka)).catch(() => undefined);
  }, [allowed, prisutstvie, userId]);

  const status = state?.status ?? null;
  const limitMs = (state?.bezdeystvie?.limit_sek ?? 0) * 1000;
  const zhdatMs = (state?.bezdeystvie?.zhdat_sek ?? 0) * 1000;
  const since = state?.since ?? null;
  const tishinaMs = tishinaV(status, since, now);
  const sledit = allowed && status === 'ready' && limitMs > 0;

  // «Готов» начался только что (вернулся с паузы, закончил обработку) — отсчёт тишины с этой минуты.
  useEffect(() => {
    if (status === 'ready') otmetitAktivnost();
  }, [status, since]);

  // Тишина пережила и срок, и время на отклик — говорим станции; она ставит «Отошёл» и пишет в чат.
  // `s818`: решаем ещё раз по свежему состоянию — вкладка из фона не видела ни разговора, ни нового «Готов».
  const pora = sledit && tishinaMs >= limitMs + zhdatMs;
  const idleAway = workplace.idleAway;
  useEffect(() => {
    if (!pora || Date.now() - soobshil.current < 15_000) return;
    soobshil.current = Date.now();
    void idleAway((svezhee) => {
      const nuzhno = ((svezhee?.bezdeystvie?.limit_sek ?? 0) + (svezhee?.bezdeystvie?.zhdat_sek ?? 0)) * 1000;
      return svezhee?.status === 'ready' && (svezhee.bezdeystvie?.limit_sek ?? 0) > 0
        && tishinaV(svezhee.status, svezhee.since ?? null, Date.now()) >= nuzhno;
    });
  }, [pora, idleAway]);

  if (!allowed || state == null) return null;

  const words = state.words?.texts ?? {};
  const word = (name: string) => words[`tel.pult.${name}`] ?? `tel.pult.${name}`;
  const cherez = (kod: string, sek: number) => word(kod).replace('{t}', formatDuration(Math.max(0, sek)));

  // 3. «Отошёл» поставила станция.
  if (status === 'away' && state.prinuditelno != null) {
    if (ostalsya === since) return null;
    const ostatsya = () => {
      setOstalsya(since);
      if (since !== null) zapomnit(ZAKRYTO, since);
    };

    return createPortal(
      <aside className="ops-skin okno-obrabotki" data-okno-obrabotki="prinuditelno" role="alertdialog" aria-label={word('forced_away')}>
        <header><span className="okno-obrabotki-znak"><IconClockPause size={22} /></span><strong>{word('forced_away')}</strong></header>
        <p>{word(state.prinuditelno === 'idle' ? 'forced_away_idle' : 'forced_away_processing')}</p>
        <footer>
          <button type="button" className="okno-obrabotki-glavnaya" disabled={busy} onClick={() => void workplace.setReady()}>{word('forced_back')}</button>
          <button type="button" onClick={ostatsya}>{word('forced_stay')}</button>
        </footer>
        {workplace.error && <p className="okno-obrabotki-oshibka" role="alert">{workplace.error}</p>}
      </aside>,
      document.body,
    );
  }

  // 1. Срок обработки вышел.
  if (status === 'processing' && secondsLeft === 0) {
    const awayAt = state.obrabotka?.away_at ? Date.parse(state.obrabotka.away_at) : null;
    const minuty = state.obrabotka?.prodlit_minuty ?? [];

    return createPortal(
      <aside className="ops-skin okno-obrabotki" data-okno-obrabotki="srok" role="alertdialog" aria-label={word('processing_over')}>
        <header><span className="okno-obrabotki-znak"><IconClockPause size={22} /></span><strong>{word('processing_over')}</strong></header>
        <p>{word('processing_over_ask')}</p>
        <footer>
          <button type="button" className="okno-obrabotki-glavnaya" disabled={busy} onClick={() => void workplace.finish()}>{word('finish')}</button>
          {minuty.map((n) => <button key={n} type="button" disabled={busy} onClick={() => void workplace.extend(n)}>{slovoMinut(word, n)}</button>)}
        </footer>
        {awayAt !== null && <p className="okno-obrabotki-srok" data-okno-obrabotki="otschyot">{cherez('processing_over_away', Math.ceil((awayAt - now) / 1000))}</p>}
      </aside>,
      document.body,
    );
  }

  // 2. Тишина в «Готов»: окно как заставка — пропадает от первого движения.
  if (sledit && tishinaMs >= limitMs) {
    return createPortal(
      <aside className="ops-skin okno-obrabotki" data-okno-obrabotki="tishina" role="alertdialog" aria-label={word('idle_ask')}>
        <header><span className="okno-obrabotki-znak"><IconClockPause size={22} /></span><strong>{word('idle_ask')}</strong></header>
        <p>{word('idle_hint')}</p>
        <footer>
          <button type="button" className="okno-obrabotki-glavnaya" onClick={() => { otmetitAktivnost(); setNow(Date.now()); }}>{word('idle_here')}</button>
        </footer>
        <p className="okno-obrabotki-srok" data-okno-obrabotki="otschyot">{cherez('idle_away', Math.ceil((limitMs + zhdatMs - tishinaMs) / 1000))}</p>
      </aside>,
      document.body,
    );
  }

  // 4. Браузер умеет видеть, что человек за компьютером, а его ещё не спрашивали.
  const segodnya = new Date(now).toDateString();
  const nikto = state.active_call == null && state.nabor == null && state.vhodyashchiy == null;
  if (sledit && nikto && prisutstvie === 'sprosit' && pozzhe !== segodnya) {
    const otlozhit = () => { setPozzhe(segodnya); zapomnit(POZZHE, segodnya); };

    return createPortal(
      <aside className="ops-skin okno-obrabotki" data-okno-obrabotki="prisutstvie" role="dialog" aria-label={word('presence_ask')}>
        <header><span className="okno-obrabotki-znak"><IconDeviceDesktop size={22} /></span><strong>{word('presence_ask')}</strong></header>
        <p>{word('presence_hint')}</p>
        <footer>
          <button type="button" className="okno-obrabotki-glavnaya" onClick={() => void razreshitPrisutstvie()}>{word('presence_allow')}</button>
          <button type="button" onClick={otlozhit}>{word('presence_later')}</button>
        </footer>
      </aside>,
      document.body,
    );
  }

  return null;
}
